/**
 * Practice Rate Cache Utility
 * 
 * Stores and retrieves rate settings (rateType, rateValue) per practice per user
 * Uses localStorage for persistence across sessions
 * 
 * Cache Key Format: {userId}_{practiceId}
 * Cache Value: { rateType, rateValue, timestamp }
 */

const CACHE_KEY = 'practice_rate_cache';

export const practiceRateCache = {
  /**
   * Get cached rate settings for a practice
   * @param {string} practiceId - The practice ID (prac_code)
   * @param {string} userId - The user ID
   * @returns {object|null} - { rateType, rateValue, timestamp } or null if not found
   */
  get: (practiceId, userId) => {
    if (!practiceId || !userId) {
      console.warn('practiceRateCache.get: Missing practiceId or userId');
      return null;
    }

    try {
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
      const key = `${userId}_${practiceId}`;
      const cached = cache[key];
      
      if (cached) {
        console.log(`Rate cache HIT for practice ${practiceId}:`, cached);
        return cached;
      }
      
      console.log(`Rate cache MISS for practice ${practiceId}`);
      return null;
    } catch (error) {
      console.error('Error reading rate cache:', error);
      return null;
    }
  },

  /**
   * Save rate settings for a practice
   * @param {string} practiceId - The practice ID (prac_code)
   * @param {string} userId - The user ID
   * @param {string} rateType - The rate type (hourly, percentage, per_patient, fixed)
   * @param {string} rateValue - The rate value
   */
  set: (practiceId, userId, rateType, rateValue) => {
    if (!practiceId || !userId) {
      console.warn('practiceRateCache.set: Missing practiceId or userId');
      return;
    }

    try {
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
      const key = `${userId}_${practiceId}`;
      
      cache[key] = {
        rateType,
        rateValue,
        timestamp: Date.now()
      };
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      console.log(`Rate cache SAVED for practice ${practiceId}:`, cache[key]);
    } catch (error) {
      console.error('Error saving to rate cache:', error);
      // Don't throw - gracefully fail
    }
  },

  /**
   * Clear cached rate for a specific practice
   * @param {string} practiceId - The practice ID (prac_code)
   * @param {string} userId - The user ID
   */
  clear: (practiceId, userId) => {
    if (!practiceId || !userId) {
      console.warn('practiceRateCache.clear: Missing practiceId or userId');
      return;
    }

    try {
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
      const key = `${userId}_${practiceId}`;
      
      delete cache[key];
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      console.log(`Rate cache CLEARED for practice ${practiceId}`);
    } catch (error) {
      console.error('Error clearing rate cache:', error);
    }
  },

  /**
   * Clear all cached rates for a user
   * @param {string} userId - The user ID
   */
  clearAll: (userId) => {
    if (!userId) {
      console.warn('practiceRateCache.clearAll: Missing userId');
      return;
    }

    try {
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
      
      Object.keys(cache).forEach(key => {
        if (key.startsWith(`${userId}_`)) {
          delete cache[key];
        }
      });
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      console.log(`Rate cache CLEARED ALL for user ${userId}`);
    } catch (error) {
      console.error('Error clearing all cache:', error);
    }
  },

  /**
   * Get all cached rates for a user (for debugging)
   * @param {string} userId - The user ID
   * @returns {object} - Map of practiceId to rate settings
   */
  getAllForUser: (userId) => {
    if (!userId) {
      console.warn('practiceRateCache.getAllForUser: Missing userId');
      return {};
    }

    try {
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
      const userCache = {};
      
      Object.keys(cache).forEach(key => {
        if (key.startsWith(`${userId}_`)) {
          const practiceId = key.replace(`${userId}_`, '');
          userCache[practiceId] = cache[key];
        }
      });
      
      return userCache;
    } catch (error) {
      console.error('Error reading all cache for user:', error);
      return {};
    }
  }
};
